"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Facebook, Instagram, Linkedin, MapPin, Mail, Phone, Send, Loader } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
function FooterMenuContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="18563" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-3/4 bg-white/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <nav className="flex flex-col gap-3">
        <Link to="#" className="hover:text-primary transition-colors w-fit font-sans text-sm md:text-base">
          Accueil
        </Link>
        <Link to="#" className="hover:text-primary transition-colors w-fit font-sans text-sm md:text-base">
          À propos
        </Link>
        <Link to="#" className="hover:text-primary transition-colors w-fit font-sans text-sm md:text-base">
          Expertises
        </Link>
        <Link to="#" className="hover:text-primary transition-colors w-fit font-sans text-sm md:text-base">
          Réalisations
        </Link>
      </nav>;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="hover:text-primary transition-colors w-fit text-sm md:text-base font-sans">
          {item.label}
        </Link>)}
    </nav>;
}
function FooterMenu() {
  return <MenuProvider menu_id="67" className="w-full">
      <FooterMenuContent />
    </MenuProvider>;
}
function NewsletterFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg text-primary text-sm font-sans">
        {successMessage}
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-2">
        <FormField control={control} name="email" rules={{
        required: "L'email est requis",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email invalide"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 space-y-0">
              <FormLabel className="sr-only">Email professionnel</FormLabel>
              <FormControl>
                <Input placeholder="Votre email professionnel" type="email" className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-primary focus-visible:border-primary h-10 rounded-lg font-sans" {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1 text-destructive font-sans" />
            </FormItem>} />
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 shrink-0 font-bold rounded-lg shadow-sm transition-all">
          {isSubmitting ? <Loader className="size-4 animate-spin" /> : <Send className="size-4" />}
          <span className="sr-only">S'abonner</span>
        </Button>
      </div>
      {submitError && <p className="text-xs text-destructive font-sans">{submitError}</p>}
      <p className="text-[10px] text-sidebar-foreground/40 font-mono uppercase tracking-wider leading-tight">
        En vous abonnant, vous acceptez notre politique de confidentialité.
      </p>
    </Form>;
}
function NewsletterSection() {
  return <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
    email: ""
  }} submitText="S'inscrire" successMessage="Merci pour votre abonnement !" className="w-full">
      <NewsletterFormContent />
    </FormProvider>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const businessName = "Freelance Com & Event";
  const address = "Ouagadougou, Pissy, en face du Lycée Vénégré au 2e étage, Secteur 27 lot 31 parcelle 20 section OA\xA0";
  const phone = "+226 07 00 00 00";
  const email = "contact@freelancecomevent.bf";
  const mapLink = "https://www.google.com/maps/search/?api=1&query=Freelance+Com+Event+Ouagadougou";
  return <footer id="pied-de-page" className="bg-sidebar text-sidebar-foreground pt-24 pb-12 border-t-2 border-primary">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          <div className="space-y-6">
            <Link to="/" className="block w-fit">
              
            </Link>
            <p className="text-sm leading-relaxed text-sidebar-foreground/70 max-w-xs font-sans">
              Positionner votre organisation comme un acteur crédible grâce à une communication stratégique et un événementiel de prestige.
            </p>
            <div className="flex items-center gap-4">
              {[{
              icon: Facebook,
              href: "https://web.facebook.com/freelancecomevents",
              label: "Facebook"
            }, {
              icon: Instagram,
              href: "#",
              label: "Instagram"
            }, {
              icon: Linkedin,
              href: "#",
              label: "LinkedIn"
            }].map((social, i) => <Link key={i} data-index={i} to={social.href} className="bg-white/5 p-2.5 rounded-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 group shadow-sm" aria-label={social.label}>
                  <social.icon className="size-5" />
                </Link>)}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-bold text-sidebar-foreground font-serif tracking-tight">Expertises</h3>
            <FooterMenu />
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-bold text-sidebar-foreground font-serif tracking-tight">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm leading-snug font-sans">{address}</p>
                  
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="size-5 text-primary shrink-0" />
                <Link to={`tel:${phone}`} className="text-sm transition-colors font-sans hover:text-primary">
                  {phone}
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="size-5 text-primary shrink-0" />
                <Link to={`mailto:${email}`} className="text-sm hover:text-primary transition-colors font-sans">
                  {email}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-bold text-sidebar-foreground font-serif tracking-tight">Newsletter</h3>
            <p className="text-sm text-sidebar-foreground/70 font-sans">
              Recevez nos analyses stratégiques et actualités événementielles directement dans votre boîte mail.
            </p>
            <NewsletterSection />
          </div>
        </div>

        <Separator className="bg-white/10 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] text-sidebar-foreground/40 font-mono uppercase tracking-widest">
          <p>&copy; {currentYear} {businessName}. Tous droits réservés.</p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="#" className="hover:text-primary transition-colors">Mentions légales</Link>
            <Link to="#" className="hover:text-primary transition-colors">Politique de confidentialité</Link>
            <Link to="#" className="hover:text-primary transition-colors">CGV</Link>
          </div>
        </div>
      </div>
    </footer>;
}